import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";

const Navbar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!(localStorage.getItem("token") || localStorage.getItem("pressToken") || localStorage.getItem("adminToken"))
  );
  const [activeTopic, setActiveTopic] = useState(null);
  const [activeCareer, setActiveCareer] = useState(null);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const [dashboardLabel, setDashboardLabel] = useState("Goto Dashboard");
  const [dashboardPath, setDashboardPath] = useState("/");
  const servicesDropdownRef = useRef(null);
  const careersDropdownRef = useRef(null);

  // Professional scroll-based navbar hide/show functionality
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      const scrollDifference = Math.abs(currentScrollPos - prevScrollPos);

      // Close mobile menu and dropdowns when scrolling
      if (scrollDifference > 10) {
        if (isOpen) {
          setIsOpen(false);
        }
        if (openDropdown) {
          setOpenDropdown(null);
        }
      }

      // Only trigger hide/show if scroll difference is significant (more than 5px)
      if (scrollDifference > 5) {
        setIsScrolling(true);

        // Hide navbar when scrolling down, show when scrolling up
        if (currentScrollPos > prevScrollPos && currentScrollPos > 100) {
          // Scrolling down and past 100px - hide navbar
          setVisible(false);
        } else {
          // Scrolling up or at top - show navbar
          setVisible(true);
        }

        setPrevScrollPos(currentScrollPos);
      }

      // Clear existing timeout
      clearTimeout(scrollTimeout);

      // Set timeout to stop scrolling state after 150ms
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [prevScrollPos, isOpen, openDropdown]);

  // Close mobile menu and dropdowns on touch/gesture events
  useEffect(() => {
    if (!isOpen && !openDropdown) return;

    let touchStartY = 0;
    let touchStartX = 0;

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;

      // Close menu when user starts touching outside the menu or dropdowns
      if (!e.target.closest('.mobile-menu-container') &&
        !e.target.closest('[id$="-dropdown"]') &&
        !e.target.closest('button[aria-expanded="true"]')) {
        setIsOpen(false);
        setOpenDropdown(null);
      }
    };

    const handleTouchMove = (e) => {
      if (!e.touches[0]) return;

      const touchCurrentY = e.touches[0].clientY;
      const touchCurrentX = e.touches[0].clientX;
      const deltaY = touchCurrentY - touchStartY;
      const deltaX = touchCurrentX - touchStartX;

      // Close menu and dropdowns on swipe gestures
      if (Math.abs(deltaY) > 50 || Math.abs(deltaX) > 50) {
        setIsOpen(false);
        setOpenDropdown(null);
      }
    };

    // Add touch event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen, openDropdown]);

  // Close dropdowns and mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isOpen) {
          setIsOpen(false);
        }
        if (openDropdown) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, openDropdown]);

  // Close dropdowns and mobile menu on screen resize
  useEffect(() => {
    const handleResize = () => {
      // Close mobile menu when screen becomes large (lg breakpoint)
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
        setOpenDropdown(null);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Close Careers dropdown when clicking outside
  useEffect(() => {
    if (openDropdown !== "careers") return;
    function handleClickOutside(event) {
      if (
        careersDropdownRef.current &&
        !careersDropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  // Resolve role from storage and set dashboard label/path
  useEffect(() => {
    const resolveRoleAndPath = () => {
      // Prefer explicit role values
      const storedRole = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
      let role = storedRole;
      // Fallbacks from stored users
      if (!role) {
        try {
          const userStr = localStorage.getItem('user');
          const user = userStr ? JSON.parse(userStr) : null;
          if (user?.role) role = String(user.role).toLowerCase();
        } catch { }
      }
      if (!role) {
        try {
          const pressUserStr = localStorage.getItem('pressUser');
          const pressUser = pressUserStr ? JSON.parse(pressUserStr) : null;
          if (pressUser?.role) role = String(pressUser.role).toLowerCase();
        } catch { }
      }

      let path = '/';
      let label = 'Dashboard';
      switch (role) {
        case 'superadmin':
        case 'subadmin':
          path = '/admin';
          label = 'Admin Dashboard';
          break;
        case 'reporter':
          path = '/reporter';
          label = 'Reporter Dashboard';
          break;
        case 'advertiser':
          path = '/advertiser';
          label = 'Advertiser Dashboard';
          break;
        case 'influencer':
          path = '/influencer';
          label = 'Influencer Dashboard';
          break;
        case 'ryvuser':
          path = '/raise';
          label = 'Raise Your Voice Dashboard';
          break;
        case 'press':
          path = '/press';
          label = 'Press Dashboard';
          break;
        case 'podcastuser':
          path = '/podcast';
          label = 'Podcast Dashboard';
          break;
        default:
          // If any dashboard token exists but role is unknown, keep generic
          break;
      }
      setDashboardLabel(label);
      setDashboardPath(path);
    };

    resolveRoleAndPath();
  }, [isLoggedIn]);

  // Close Services dropdown when clicking outside
  useEffect(() => {
    if (openDropdown !== "services") return;
    function handleClickOutside(event) {
      if (
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const services = [
    { id: "reporting", name: "Reporting", link: "/ReportingServices" },
    { id: "advertising", name: "Advertising", link: "/AdvertisingServices" },
    { id: "influencing", name: "Influencing", link: "/InfluencingServices" },
    {
      id: "socialMedia",
      name: "Social Media Marketing",
      link: "/SocialMediaServices",
    },
    {
      id: "campaignManagement",
      name: "Campaign Management",
      link: "/CampaignManagementServices",
    },
    { id: "brandLaunch", name: "Brand Launch", link: "/BrandLaunchServices" },
    { id: "survey", name: "Survey", link: "/SurveyServices" },
  ];

  const careers = [
    { id: "reporter", name: "Reporter", link: "/register?role=Reporter" },
    { id: "advertiser", name: "Advertiser", link: "/register?role=Advertiser" },
    { id: "marketer", name: "Marketer", link: "/marketer" },
    { id: "influencer", name: "Influencer", link: "/register?role=Influencer" },
    { id: "drLawyer", name: "Dr, Lawyer", link: "/JoinIinsaf" },
  ];

  const closeNavAndNavigate = () => {
    setIsOpen(false);
    setOpenDropdown(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("pressToken");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("user");
    localStorage.removeItem("pressUser");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("role");
    localStorage.removeItem("userRole");
    setIsLoggedIn(false);
    navigate("/");
  };

  const goToProfile = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/goto/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Profile API response:", response);
      const user = response.data && response.data.data ? response.data.data : response.data;
      if (
        user &&
        (user._id || user.id) &&
        user.name &&
        user.email &&
        user.role &&
        typeof user.walletBalance !== 'undefined' &&
        user.createdAt &&
        user.updatedAt
      ) {
        // Debug: log user and role
        console.log('User object:', user);
        const role = typeof user.role === 'string' ? user.role.toLowerCase() : '';

        if (role === 'superadmin' || role === 'subadmin') {
          navigate('/admin');
        } else if (role === 'reporter') {
          navigate('/reporter');
        } else if (role === 'advertiser') {
          navigate('/advertiser');
        } else if (role === 'marketer') {
          navigate('/marketer');
        } else if (role === 'influencer') {
          navigate('/influencer');
        } else if (role === 'press') {
          navigate('/press');
        } else if (role === 'podcastuser') {
          navigate('/podcast');
        } else {
          // Fallback to stored role in localStorage
          const storedRole = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
          if (storedRole === 'superadmin' || storedRole === 'subadmin') navigate('/admin');
          else if (storedRole === 'reporter') navigate('/reporter');
          else if (storedRole === 'advertiser') navigate('/advertiser');
          else if (storedRole === 'influencer') navigate('/influencer');
          else if (storedRole === 'press') navigate('/press');
          else if (storedRole === 'podcastuser') navigate('/podcast');
          else navigate('/');
        }
      }
    } catch (error) {
      console.error('Profile API error:', error);
      const storedRole = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
      if (storedRole === 'superadmin' || storedRole === 'subadmin') navigate('/admin');
      else if (storedRole === 'reporter') navigate('/reporter');
      else if (storedRole === 'advertiser') navigate('/advertiser');
      else if (storedRole === 'influencer') navigate('/influencer');
      else if (storedRole === 'press') navigate('/press');
      else if (storedRole === 'podcastuser') navigate('/podcast');
      else navigate('/');
    }
  };

  return (
    <>
      <nav
        className={`bg-white/80 backdrop-blur-md shadow-xl border border-white/20 fixed w-[95%] md:w-[90%] lg:w-[85%] xl:w-[75%] left-1/2 -translate-x-1/2 rounded-full z-[100] transition-all duration-500 ease-in-out ${visible
          ? "top-4 opacity-100 translate-y-0"
          : "-top-28 opacity-0 -translate-y-4"
          } ${isScrolling ? "shadow-2xl backdrop-blur-lg bg-white/95 border-white/30" : ""
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo Section */}
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <img
                  src="/images/iinsaf.png"
                  alt="IINSAF Logo"
                  className="h-10 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              <Link
                to="/"
                className="relative text-gray-700 hover:text-blue-600 transition-all duration-300 text-sm font-semibold group"
              >
                <span className="relative z-10">Home</span>
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link
                to="/contact"
                className="relative text-gray-700 hover:text-blue-600 transition-all duration-300 text-sm font-semibold group"
              >
                <span className="relative z-10">Contact</span>
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-300"></div>
              </Link>

              {/* Services Dropdown */}
              <div className="relative" ref={servicesDropdownRef}>
                <button
                  type="button"
                  className="relative flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-all duration-300 text-sm font-semibold group"
                  onClick={() =>
                    setOpenDropdown(
                      openDropdown === "services" ? null : "services"
                    )
                  }
                  aria-expanded={openDropdown === "services"}
                  aria-controls="services-dropdown"
                >
                  <span className="relative z-10">Services</span>
                  <FontAwesomeIcon
                    icon={
                      openDropdown === "services" ? faChevronUp : faChevronDown
                    }
                    className="h-4 w-4 ml-1 transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-300"></div>
                </button>
                {openDropdown === "services" && (
                  <div
                    id="services-dropdown"
                    className="absolute top-full left-0 mt-3 w-72 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl py-3 animate-fadeIn border border-white/20 z-50"
                  >
                    <div className="max-h-[80vh] overflow-y-auto">
                      <ul className="grid grid-cols-1 gap-1 p-2">
                        {services.map((service) => (
                          <li
                            key={service.id}
                            onClick={() => setActiveTopic(service.id)}
                            className="cursor-pointer hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 px-4 py-3 rounded-xl transition-all duration-300 group"
                          >
                            <Link
                              to={service.link}
                              className="text-gray-700 hover:text-blue-600 text-base font-medium block w-full text-left group-hover:translate-x-1 transition-all duration-300"
                              onClick={() => setOpenDropdown(null)}
                            >
                              {service.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Careers Dropdown */}
              <div className="relative" ref={careersDropdownRef}>
                <button
                  type="button"
                  className="relative flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-all duration-300 text-sm font-semibold group"
                  onClick={() =>
                    setOpenDropdown(openDropdown === "careers" ? null : "careers")
                  }
                  aria-expanded={openDropdown === "careers"}
                  aria-controls="careers-dropdown"
                >
                  <span className="relative z-10">Careers</span>
                  <FontAwesomeIcon
                    icon={
                      openDropdown === "careers" ? faChevronUp : faChevronDown
                    }
                    className="h-4 w-4 ml-1 transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-300"></div>
                </button>
                {openDropdown === "careers" && (
                  <div
                    id="careers-dropdown"
                    className="absolute top-full left-0 mt-3 w-72 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl py-3 animate-fadeIn border border-white/20 z-50"
                  >
                    <div className="max-h-[80vh] overflow-y-auto">
                      <ul className="grid grid-cols-1 gap-1 p-2">
                        {careers.map((career) => (
                          <li
                            key={career.id}
                            onClick={() => setActiveCareer(career.id)}
                            className="cursor-pointer hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 px-4 py-3 rounded-xl transition-all duration-300 group"
                          >
                            <Link
                              to={career.link}
                              className="text-gray-700 hover:text-blue-600 text-base font-medium block w-full text-left group-hover:translate-x-1 transition-all duration-300"
                              onClick={() => setOpenDropdown(null)}
                            >
                              {career.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Auth Buttons */}
              {isLoggedIn ? (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      if (dashboardPath && dashboardPath !== '/') navigate(dashboardPath);
                      else goToProfile();
                    }}
                    className="group inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 rounded-full shadow-lg hover:from-blue-700 hover:via-blue-800 hover:to-indigo-700 active:scale-[0.98] transition-all duration-300 hover:shadow-xl"
                  >
                    <svg
                      className="w-3.5 h-3.5 text-white opacity-90 group-hover:opacity-100 group-hover:rotate-12 transition-all duration-300"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
                    </svg>
                    <span>{dashboardLabel}</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="px-5 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-full transition-all duration-300 shadow-lg shadow-indigo-200/50 hover:shadow-xl"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="px-5 py-2 text-sm font-bold text-blue-600 border-2 border-indigo-100 bg-indigo-50/50 rounded-full hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-all duration-300"
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden p-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 group"
            >
              <div className="w-6 h-0.5 bg-gray-600 mb-1.5 transition-all duration-300 group-hover:bg-blue-600"></div>
              <div className="w-6 h-0.5 bg-gray-600 mb-1.5 transition-all duration-300 group-hover:bg-blue-600"></div>
              <div className="w-6 h-0.5 bg-gray-600 transition-all duration-300 group-hover:bg-blue-600"></div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-[120]">
          {/* Overlay for click outside to close */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={toggleMenu}
          ></div>

          {/* Floating Mobile Menu */}
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 w-[95%] sm:w-[80%] max-h-[95vh] bg-white/95 backdrop-blur-xl shadow-2xl shadow-indigo-900/20 border border-white/20 overflow-y-auto mobile-menu-container rounded-[2rem]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 relative">
              {/* Close Button */}
              <button
                onClick={toggleMenu}
                className="absolute top-5 right-5 p-2 rounded-full bg-slate-100 hover:bg-red-50 transition-all duration-300 group z-10"
              >
                <svg
                  className="w-4 h-4 text-slate-500 group-hover:text-red-600 transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Mobile Logo */}
              <Link
                to="/"
                className="flex items-center justify-center mb-6 group"
                onClick={closeNavAndNavigate}
              >
                <div className="relative mt-2">
                  <img
                    src="/images/iinsaf.png"
                    alt="IINSAF Logo"
                    className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              </Link>

              {/* Mobile Navigation Links */}
              <div className="flex flex-col space-y-2">
                <Link
                  to="/"
                  className="text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/50 py-2.5 px-4 text-center text-sm font-bold transition-all duration-300 rounded-xl"
                  onClick={closeNavAndNavigate}
                >
                  Home
                </Link>
                <Link
                  to="/contact"
                  className="text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/50 py-2.5 px-4 text-center text-sm font-bold transition-all duration-300 rounded-xl"
                  onClick={closeNavAndNavigate}
                >
                  Contact
                </Link>

                {/* Mobile Services Dropdown */}
                <div className="relative" ref={servicesDropdownRef}>
                  <button
                    onClick={() =>
                      setOpenDropdown(openDropdown === "services" ? null : "services")
                    }
                    className="w-full flex items-center justify-center space-x-2 text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/50 py-2.5 px-4 text-sm font-bold transition-all duration-300 rounded-xl"
                  >
                    <span>Services</span>
                    <FontAwesomeIcon
                      icon={openDropdown === "services" ? faChevronUp : faChevronDown}
                      className="h-3 w-3 transition-transform duration-300"
                    />
                  </button>
                  {openDropdown === "services" && (
                    <div className="bg-slate-50/50 rounded-xl mt-1 border border-slate-100">
                      {services.map((service) => (
                        <Link
                          key={service.id}
                          to={service.link}
                          className="block px-4 py-2 text-slate-600 hover:bg-white hover:text-indigo-600 transition-all duration-300 rounded-lg mx-1 my-1 text-[13px] font-semibold text-center"
                          onClick={() => {
                            setActiveTopic(service.id);
                            closeNavAndNavigate();
                          }}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile Careers Dropdown */}
                <div className="relative" ref={careersDropdownRef}>
                  <button
                    onClick={() =>
                      setOpenDropdown(openDropdown === "careers" ? null : "careers")
                    }
                    className="w-full flex items-center justify-center space-x-2 text-slate-700 hover:text-indigo-600 hover:bg-indigo-50/50 py-2.5 px-4 text-sm font-bold transition-all duration-300 rounded-xl"
                    type="button"
                    aria-expanded={openDropdown === "careers"}
                    aria-controls="careers-dropdown"
                  >
                    <span>Careers</span>
                    <FontAwesomeIcon
                      icon={openDropdown === "careers" ? faChevronUp : faChevronDown}
                      className="h-3 w-3 transition-transform duration-300"
                    />
                  </button>
                  {openDropdown === "careers" && (
                    <div className="bg-slate-50/50 rounded-xl mt-1 border border-slate-100">
                      {careers.map((career) => (
                        <Link
                          key={career.id}
                          to={career.link}
                          className="block px-4 py-2 text-slate-600 hover:bg-white hover:text-indigo-600 transition-all duration-300 rounded-lg mx-1 my-1 text-[13px] font-semibold text-center"
                          onClick={() => {
                            setActiveCareer(career.id);
                            setOpenDropdown(null);
                            closeNavAndNavigate();
                          }}
                        >
                          {career.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mobile Auth Buttons */}
                <div className="pt-4 border-t border-slate-100">
                  {isLoggedIn ? (
                    <div className="flex flex-col space-y-2.5">
                      <button
                        onClick={() => {
                          if (dashboardPath && dashboardPath !== '/') navigate(dashboardPath);
                          else goToProfile();
                          closeNavAndNavigate();
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full text-sm font-bold shadow-lg hover:shadow-xl active:scale-[0.98] transition-all duration-300"
                      >
                        <svg
                          className="w-4 h-4 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
                        </svg>
                        <span>{dashboardLabel}</span>
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          closeNavAndNavigate();
                        }}
                        className="w-full px-5 py-2.5 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full text-sm font-bold transition-all duration-300"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-2.5">
                      <Link
                        to="/login"
                        onClick={closeNavAndNavigate}
                        className="w-full px-5 py-2.5 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full text-sm font-bold text-center shadow-lg transition-all duration-300"
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        onClick={closeNavAndNavigate}
                        className="w-full px-5 py-2.5 text-indigo-600 border-2 border-indigo-100 bg-indigo-50/50 rounded-full hover:bg-indigo-50 text-center text-sm font-bold transition-all duration-300"
                      >
                        Register
                      </Link>
                    </div>
                  )}

                  {/* Quick Access Buttons */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-3">Quick Access</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Link
                        to="/press/login"
                        className="flex flex-col items-center justify-center gap-1.5 p-2 bg-blue-50/50 hover:bg-blue-50 border border-blue-100/50 rounded-xl transition-colors text-center"
                        onClick={closeNavAndNavigate}
                      >
                        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        <span className="text-[11px] font-bold text-blue-700">Press</span>
                      </Link>
                      <Link
                        to="/podcast/login"
                        className="flex flex-col items-center justify-center gap-1.5 p-2 bg-purple-50/50 hover:bg-purple-50 border border-purple-100/50 rounded-xl transition-colors text-center"
                        onClick={closeNavAndNavigate}
                      >
                        <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        <span className="text-[11px] font-bold text-purple-700">Podcast</span>
                      </Link>
                      <Link
                        to="/raise/login"
                        className="flex flex-col items-center justify-center gap-1.5 p-2 bg-emerald-50/50 hover:bg-emerald-50 border border-emerald-100/50 rounded-xl transition-colors text-center col-span-2"
                        onClick={closeNavAndNavigate}
                      >
                        <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        <span className="text-[11px] font-bold text-emerald-700">Raise Your Voice</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;